import { type ChangeEvent, type SubmitEvent, useEffect, useState } from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import { Button } from '@depop/web-ui-kit/Button';
import { useTranslations } from 'next-intl';

import { OfferModalProductSummary } from '../../OfferModalProductSummary';
import { OfferHistory } from '../OfferHistory';

import styles from './styles.module.css';

import { OfferModalInput } from '@/components/OfferModalInput';
import { COUNTER_OFFER_TRANSLATION_KEYS } from '@/constants/offers';
import { useDebounce } from '@/modules/debounce/useDebounce';
import {
  type OfferErrorResponse,
  type OfferPriceQuoteResponse,
} from '@/modules/offers/buyers/aliases';
import { isValidOfferValue } from '@/modules/offers/buyers/helpers/isValidOfferValue';
import { useCounterOfferMutation } from '@/modules/offers/buyers/hooks/useCounterOfferMutation';
import { useCounterOfferPriceQuoteMutation } from '@/modules/offers/buyers/hooks/useCounterOfferPriceQuoteMutation';
import { useOffersTracking } from '@/modules/offers/buyers/hooks/useOffersTracking';
import {
  type CounterOfferProductPrice,
  OfferRole,
} from '@/modules/offers/buyers/types';
import { DEBOUNCE_PRICE_QUOTE_DELAY } from '@/modules/offers/constants';

type Props = {
  currencySymbol: string;
  productId: number;
  offerId: string;
  productPrice: CounterOfferProductPrice;
  userRole: OfferRole;
  onSuccessfulCounterOffer: () => void;
  isOpen: boolean;
  imageHref?: string;
  variantId?: number;
};

export function CounterOfferModalForm({
  currencySymbol,
  productId,
  offerId,
  productPrice,
  userRole,
  onSuccessfulCounterOffer,
  isOpen,
  imageHref,
  variantId,
}: Props) {
  const [offerPrice, setOfferPrice] = useState('');
  const [priceQuote, setPriceQuote] = useState<OfferPriceQuoteResponse | null>(
    null
  );
  const [error, setError] = useState('');
  const [counterOfferFeeQuoteError, setCounterOfferFeeQuoteError] =
    useState<OfferErrorResponse>();
  const offersT = useTranslations('offers');
  const { sendCounterOfferFormSubmitEvent } = useOffersTracking();
  const debouncedOfferPrice = useDebounce(
    offerPrice,
    DEBOUNCE_PRICE_QUOTE_DELAY
  );
  const isBuyer = userRole === OfferRole.Buyer;
  const offerValid = isValidOfferValue(offerPrice);

  function handleCounterOfferMutationError(mutationError: OfferErrorResponse) {
    if (!mutationError?.id) {
      return setError(offersT('Error.GENERIC_ERROR'));
    }

    if (mutationError.id) {
      if (mutationError.id === 'OFFER_BELOW_FLOOR_PRICE') {
        return setError(
          offersT(`Error.${mutationError.id}`, {
            floorPercentage: mutationError.payload?.action || '',
          })
        );
      }

      const key =
        COUNTER_OFFER_TRANSLATION_KEYS[mutationError.id] ||
        'Error.GENERIC_ERROR';

      setError(offersT(key));
    }
  }

  const counterOfferMutation = useCounterOfferMutation({
    offerId,
    onSuccess: onSuccessfulCounterOffer,
    onError: handleCounterOfferMutationError,
  });

  const counterOfferQuoteMutation = useCounterOfferPriceQuoteMutation({
    offerId,
    onSuccess: setPriceQuote,
    onError: (mutationError: OfferErrorResponse) => {
      setPriceQuote(null);
      setCounterOfferFeeQuoteError(mutationError);
      handleCounterOfferMutationError(mutationError);
    },
  });

  function handleOfferPriceChange(e: ChangeEvent<HTMLInputElement>) {
    setOfferPrice(e.target.value);
    setError('');
    setPriceQuote(null);
    setCounterOfferFeeQuoteError(undefined);
  }

  useEffect(() => {
    if (isValidOfferValue(debouncedOfferPrice) && isBuyer) {
      counterOfferQuoteMutation.mutate({
        product_id: productId,
        offer_currency: productPrice.currencyName,
        offer_value: debouncedOfferPrice,
        ...(variantId ? { variant_id: variantId } : {}),
      });
    }
  }, [debouncedOfferPrice]);

  useEffect(() => {
    if (!isOpen) {
      setError('');
      setOfferPrice('');
      setPriceQuote(null);
      setCounterOfferFeeQuoteError(undefined);
    }
  }, [isOpen]);

  function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault();

    sendCounterOfferFormSubmitEvent({
      productId,
      offerId,
      productPrice,
      role: userRole,
      offerPrice,
    });

    counterOfferMutation.mutate({
      product_id: productId,
      offer_value: offerPrice,
      offer_currency: productPrice.currencyName,
      ...(variantId ? { variant_id: variantId } : {}),
    });
  }

  const isPriceQuoteVisible =
    isBuyer &&
    Boolean(priceQuote) &&
    Boolean(offerPrice) &&
    !error &&
    !counterOfferQuoteMutation.isPending &&
    offerValid;

  const isSubmitDisabled =
    !offerValid ||
    counterOfferQuoteMutation.isPending ||
    Boolean(counterOfferFeeQuoteError?.id);

  return (
    <form onSubmit={handleSubmit}>
      <OfferModalProductSummary
        currencySymbol={currencySymbol}
        productPrice={productPrice}
        imageHref={imageHref}
      />
      <OfferHistory
        currencySymbol={currencySymbol}
        userRole={userRole}
        currentPrice={productPrice.baseCurrentPrice}
        offerersHighestOffer={productPrice.offerersHighestOffer}
        sellersLowestOffer={productPrice.sellersLowestOffer}
      />
      <OfferModalInput
        currencySymbol={currencySymbol}
        labelText={offersT('CounterOfferModal.NewCounterAmountLabel')}
        offerPrice={offerPrice}
        errorText={error}
        placeholder={productPrice.baseCurrentPrice}
        onChange={handleOfferPriceChange}
        quote={isPriceQuoteVisible ? priceQuote?.quote : undefined}
        quotePriceComponents={priceQuote?.quote_price_components}
      />
      <Button
        className={styles.counterOfferModalFormSubmitButton}
        disabled={isSubmitDisabled}
        type="submit"
      >
        {offersT('SendOffer')}
      </Button>
      {userRole === OfferRole.Buyer && (
        <Caption
          variant="lg-regular"
          className={styles.counterOfferModalFormTipText}
        >
          {offersT('CounterOfferModal.TipText')}
        </Caption>
      )}
    </form>
  );
}
